import { useUser } from "@/queries/user"
import { WidgetWrapper } from "./WidgetWrapper"
import { WidgetCommonFTUContainer } from "@/components/widgets/WidgetCommonContainer"
import BottomIcon from "@/assets/clipart/widgets/rewards-bottom-icon-coin.svg"
import { View, Linking } from "react-native"
import { $authToken } from "@/stores/auth"
import config from "@/config"
import { NewBadge } from "@/components/widgets/NewBadge"
import BFText, { H3, H5, Tiny } from "@/components/standard/BFText"
import IconCheck from "phosphor-react-native/src/icons/Check"
import { BFColors } from "@/constants/Colors"

interface DealProgressIndicatorProps {
  number: number
  isCompleted: boolean
}

const DealProgressIndicator: React.FC<DealProgressIndicatorProps> = ({
  number,
  isCompleted,
}: DealProgressIndicatorProps) => {
  return (
    <View
      className="items-center justify-center rounded-sm w-[24px] h-[20px] mr-[2px]"
      style={{
        backgroundColor: isCompleted ? BFColors.teal : BFColors.calmBlue,
      }}
    >
      {isCompleted && (
        <IconCheck size={12} color={BFColors.midnightBlue} weight="bold" />
      )}
      {!isCompleted && (
        <BFText className="text-white text-[10px] font-figtree-bold">
          {number}
        </BFText>
      )}
    </View>
  )
}

interface RewardsProgressContainerProps {
  dealCount: number
}

const RewardsProgressContainer: React.FC<RewardsProgressContainerProps> = ({
  dealCount,
}: RewardsProgressContainerProps) => {
  const maxDeals = 5
  const currentDiscount = Math.min(dealCount, maxDeals) * 0.1

  return (
    <View className="items-start justify-start flex-1">
      <H5 className="text-white font-figtree-bold mb-2">
        {dealCount} loan{dealCount !== 1 ? "s" : ""} closed
      </H5>

      <View className="flex-row self-stretch justify-between mb-2">
        {[1, 2, 3, 4, 5].map((num) => (
          <DealProgressIndicator
            key={num}
            number={num}
            isCompleted={num <= dealCount}
          />
        ))}
      </View>

      <H3 className="text-teal font-figtree-extrabold">
        .{currentDiscount.toFixed(2).split(".")[1]}% discount
      </H3>
      <Tiny className="text-muted mb-1">on origination fee</Tiny>
      <View className="self-end">
        <BottomIcon width={48} height={48} />
      </View>
    </View>
  )
}

export default function RewardsWidget() {
  const { data: user } = useUser()
  const isRewardsOptedIn = user?.me?.rewardsOptInStatus ?? false
  const rewardsDealCount = user?.me?.rewardsDealCount ?? 0

  const onPress = () => {
    void Linking.openURL(
      `${config.DASHBOARD_URL}/rewards/?token=${$authToken.get()}`
    )
  }

  let variant: "new" | "default" = "new"
  let showDealProgress = false
  let headerText = "Rewards just got an update"
  let subTitle = "Opt in to earn up to .50% off your next loan"
  if (isRewardsOptedIn) {
    variant = "default"
    headerText = "Flip more, save more"
    subTitle = "Earn up to .50% off your next loan"
    if (rewardsDealCount > 0) {
      showDealProgress = true
    }
  }

  return (
    <WidgetWrapper
      title="Rewards"
      category="rewards"
      orientation="vertical"
      variant={variant}
      onPress={onPress}
    >
      {!showDealProgress ? (
        <WidgetCommonFTUContainer
          headerText={headerText}
          subTitle={subTitle}
          TopComponent={variant === "new" ? <NewBadge /> : undefined}
          BottomIconComponent={variant === "default" ? BottomIcon : undefined}
          variant={variant}
        />
      ) : (
        <RewardsProgressContainer dealCount={rewardsDealCount} />
      )}
    </WidgetWrapper>
  )
}
